import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiArrowLeft } from '@mdi/js';
import { getAppById } from '../../data/apps';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { Container } from '../ui/Container';
import { CTASection } from '../sections/CTASection';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
`;

const HeroSection = styled.section<{ $color: string }>`
  padding: var(--space-20) 0 var(--space-16);
  background: linear-gradient(
    135deg,
    ${props => props.$color}18 0%,
    transparent 60%
  );
  position: relative;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  margin-bottom: var(--space-8);
  text-decoration: none;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--color-text-primary);
  }
`;

const HeroInner = styled.div`
  max-width: 760px;
`;

const AppIconLarge = styled.div<{ $color: string }>`
  width: 84px;
  height: 84px;
  border-radius: var(--radius-xl);
  background: ${props => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-6);
  overflow: hidden;
`;

const AppLogoImg = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const Title = styled.h1`
  font-size: var(--text-5xl);
  margin-bottom: var(--space-3);
  letter-spacing: -0.02em;
`;

const Tagline = styled.p`
  font-size: var(--text-xl);
  color: var(--color-text-secondary);
  margin-bottom: var(--space-8);
  max-width: 640px;
  line-height: var(--leading-relaxed);
`;

const Lede = styled.p`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--color-text-primary);
  max-width: 720px;
`;

const Strong = styled.span`
  color: var(--color-text-primary);
  font-weight: var(--font-semibold);
`;

const ProduceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProduceCard = styled(GlassCard)`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const ProduceTitle = styled.h3`
  font-size: var(--text-xl);
  margin: 0;
`;

const ProduceBody = styled.p`
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--color-text-secondary);
  margin: 0;
`;

const Closer = styled.p`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--color-text-primary);
  max-width: 760px;
  margin: var(--space-8) auto 0;
  text-align: center;
`;

const WorkflowList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: var(--space-5);
  counter-reset: workflow;
  max-width: 880px;
  margin-left: auto;
  margin-right: auto;
`;

const StepCard = styled.li<{ $color: string }>`
  position: relative;
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: var(--space-5);
  align-items: start;
  padding: var(--space-6);
  background: var(--glass-background);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  counter-increment: workflow;

  &::before {
    content: counter(workflow, decimal-leading-zero);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: var(--radius-lg);
    background: ${props => props.$color}18;
    color: ${props => props.$color};
    font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    letter-spacing: 0.04em;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: calc(var(--space-6) + 28px);
    top: 100%;
    width: 1px;
    height: var(--space-5);
    background: var(--color-border);
  }

  @media (max-width: 640px) {
    grid-template-columns: 44px 1fr;
    gap: var(--space-4);
    padding: var(--space-5);

    &::before {
      width: 44px;
      height: 44px;
      font-size: var(--text-base);
    }

    &:not(:last-child)::after {
      left: calc(var(--space-5) + 22px);
    }
  }
`;

const StepBody = styled.div``;

const StepTitle = styled.h3`
  font-size: var(--text-xl);
  margin: 0 0 var(--space-2);
`;

const StepText = styled.p`
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--color-text-secondary);
  margin: 0;
`;

const workflowSteps = [
  {
    title: 'Specification',
    body:
      'A written description of the software to build — what it should do, how it should behave, the surface it should present to its users.',
  },
  {
    title: 'Designed and built',
    body:
      'Substrate produces the application end-to-end from the specification: the interface the user sees, the application code, and the backend that runs it — under human direction, without anyone writing the application code by hand.',
  },
  {
    title: 'Deployed and running',
    body:
      'The finished software goes live and is reachable to its intended users. The backend is up, the interface is open, the application is in use.',
  },
  {
    title: 'Iteration',
    body:
      'New specifications evolve the running software. Substrate carries each change through the same arc — designed, built, deployed — and the live application keeps moving.',
  },
];

export function Substrate() {
  const app = getAppById('substrate');
  const accent = app?.accentColor ?? '#00d4aa';
  const logo = app?.logo ?? '/assets/substrate-logo.svg';

  const description =
    "Substrate is Data 26's autonomous software-development system. It takes a written specification and produces deployed, working software — directed by a human, without manual coding.";

  return (
    <PageWrapper>
      <PageHead
        title="Substrate — Lancaster Systems Automation's autonomous software-development system"
        description={description}
        ogImage={`https://portfolio.data26.solutions${logo}`}
      />

      <HeroSection $color={accent}>
        <Container>
          <BackLink to="/products">
            <Icon path={mdiArrowLeft} size={0.8} />
            Back to Products
          </BackLink>

          <HeroInner>
            <AppIconLarge $color={accent}>
              <AppLogoImg src={logo} alt="Substrate logo" />
            </AppIconLarge>

            <Title>Substrate</Title>
            <Tagline>
              Lancaster Systems Automation's autonomous software-development system.
            </Tagline>

            <Lede>
              <Strong>Substrate is Data 26's autonomous software-development system.</Strong>{' '}
              It takes a written specification and produces deployed, working software
              — directed by a human, without manual coding.
            </Lede>
          </HeroInner>
        </Container>
      </HeroSection>

      <Section>
        <SectionHeader
          title="What Substrate produces"
          subtitle="Real software, designed and deployed live from a specification."
          centered
        />
        <ProduceGrid>
          <ProduceCard>
            <ProduceTitle>Full-stack web applications</ProduceTitle>
            <ProduceBody>
              Complete web applications, with their backends — designed, built,
              and taken live so they serve real users.
            </ProduceBody>
          </ProduceCard>
          <ProduceCard>
            <ProduceTitle>Native iOS clients</ProduceTitle>
            <ProduceBody>
              Native iOS applications, with their backends — designed, built,
              and deployed so the client runs on device against a live service.
            </ProduceBody>
          </ProduceCard>
        </ProduceGrid>
        <Closer>
          Each is produced from a specification under human direction. No one writes
          the application code by hand.
        </Closer>
      </Section>

      <Section background="secondary">
        <SectionHeader
          title="The workflow"
          subtitle="From a written specification to a running application, then through each iteration after it."
          centered
        />
        <WorkflowList>
          {workflowSteps.map(step => (
            <StepCard key={step.title} $color={accent}>
              <StepBody>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.body}</StepText>
              </StepBody>
            </StepCard>
          ))}
        </WorkflowList>
      </Section>

      <CTASection
        title="Talk to Lancaster Systems Automation"
        subtitle="About what Substrate could build for your organization."
        primaryCTA={{ label: 'Contact Us', to: '/contact' }}
        secondaryCTA={{ label: 'See Our Products', to: '/products' }}
      />
    </PageWrapper>
  );
}

export default Substrate;
